/**
 * Types pour le module Écritures Récurrentes
 * Conforme au backend recurring-entries controller
 * 
 * Automatisation des écritures répétitives (loyers, salaires, abonnements)
 * via CRON quotidien — zéro oubli, gain de temps significatif
 */

// ====== ENUMS ======

/** Fréquence de récurrence */
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'semi_annual' | 'annual';

// ====== INTERFACES ======

/** Ligne modèle d'une écriture récurrente */
export interface RecurringEntryTemplateLine {
  accountId: string;
  accountCode?: string;
  accountName?: string;
  debit: number;
  credit: number;
  description: string;
  analyticCode?: string | null;
  vatCode?: string | null;
  vatAmount?: number | null;
}

/** Écriture récurrente complète (réponse API) */
export interface RecurringEntry {
  id: string;
  name: string;
  description?: string;
  frequency: RecurrenceFrequency;
  nextRunDate: string;
  endDate?: string | null;
  isActive: boolean;
  lastRunDate?: string | null;
  fiscalYearId?: string;
  journalType?: string;
  referenceTemplate?: string;
  templateLines: RecurringEntryTemplateLine[];
  companyId?: string;
  businessUnitId?: string;
  createdAt: string;
  updatedAt?: string;
}

/** Payload de création d'une écriture récurrente */
export interface CreateRecurringEntryPayload {
  name: string;
  description?: string;
  frequency: RecurrenceFrequency;
  nextRunDate: string;
  endDate?: string;
  fiscalYearId?: string;
  journalType?: string;
  referenceTemplate?: string;
  templateLines: RecurringEntryTemplateLine[];
}

/** Payload de mise à jour d'une écriture récurrente */
export interface UpdateRecurringEntryPayload {
  name?: string;
  description?: string;
  frequency?: RecurrenceFrequency;
  nextRunDate?: string;
  endDate?: string | null;
  isActive?: boolean;
  fiscalYearId?: string;
  journalType?: string;
  referenceTemplate?: string;
  templateLines?: RecurringEntryTemplateLine[];
}

/** Détail d'une écriture générée */
export interface GeneratedEntryDetail {
  recurringEntryId: string;
  recurringEntryName: string;
  journalId: string;
  date: string;
}

/** Résultat de la génération des écritures récurrentes */
export interface GenerateRecurringResult {
  generated: number;
  details: GeneratedEntryDetail[];
}

// ====== LABELS & HELPERS ======

export const RECURRENCE_FREQUENCY_LABELS: Record<RecurrenceFrequency, string> = {
  daily: 'Quotidien',
  weekly: 'Hebdomadaire',
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  semi_annual: 'Semestriel',
  annual: 'Annuel',
};

export const RECURRENCE_FREQUENCY_COLORS: Record<RecurrenceFrequency, { bg: string; text: string }> = {
  daily:       { bg: 'bg-red-100 dark:bg-red-900/30',     text: 'text-red-700 dark:text-red-400' },
  weekly:      { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400' },
  monthly:     { bg: 'bg-blue-100 dark:bg-blue-900/30',    text: 'text-blue-700 dark:text-blue-400' },
  quarterly:   { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400' },
  semi_annual: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-400' },
  annual:      { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400' },
};
